import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';

const CATALOG_STATUSES = [
  { key: '', label: 'All Catalog Statuses' },
  { key: 'pending_review', label: 'Pending Review' },
  { key: 'active', label: 'Catalog Active' },
  { key: 'inactive', label: 'Catalog Inactive' },
  { key: 'suppressed', label: 'Suppressed' },
];

const MARKET_STATUSES = [
  { key: '', label: 'All Lifecycle Statuses' },
  { key: 'active', label: 'Active' },
  { key: 'launch_ready', label: 'Launch Ready' },
  { key: 'priority_development', label: 'Priority Development' },
  { key: 'developing', label: 'Developing' },
  { key: 'passive', label: 'Passive' },
  { key: 'out_of_scope', label: 'Out Of Scope' },
];

function titleCase(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value || 0).toLocaleString('en-US');
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatShortDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function metricValue(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return 0;
}

function MetricCard({ title, value, caption, onClick }) {
  const Tag = onClick ? 'button' : 'article';
  return (
    <Tag className={`ops-card${onClick ? ' ops-card-action' : ''}`} type={onClick ? 'button' : undefined} onClick={onClick}>
      <div className="ops-card-title">{title}</div>
      <div className="ops-card-value">{value}</div>
      <div className="ops-card-caption">{caption}</div>
    </Tag>
  );
}

function Badge({ children, tone = 'neutral' }) {
  return <span className={`ops-market-badge ${tone}`}>{children}</span>;
}

function authRedirect(navigate, err) {
  if (err?.status === 401 || err?.status === 403) {
    clearAuthToken();
    navigate('/login', { replace: true });
    return true;
  }
  return false;
}

function formatAliasType(value) {
  const key = String(value || '').trim();
  const labels = {
    zip: 'ZIP alias',
    locality: 'Locality alias',
    legacy_string: 'Legacy locality key',
    system_backfill: 'Backfill import',
    external_market: 'External market key',
    county: 'County alias',
    state: 'State alias',
  };
  return labels[key] || titleCase(key);
}

function formatAliasSource(value) {
  const key = String(value || '').trim();
  const labels = {
    manual_admin: 'Manual admin',
    system_backfill: 'Backfill import',
    legacy_string: 'Legacy import',
    hotspot_create: 'Hotspot create',
  };
  return labels[key] || titleCase(key);
}

function formatAuditEvent(value) {
  const key = String(value || '').trim();
  const labels = {
    market_created_from_hotspot: 'Market created',
    market_alias_upserted: 'Alias added',
    market_alias_deleted: 'Alias removed',
    market_catalog_status_changed: 'Catalog status changed',
    market_status_transition: 'Lifecycle changed',
    market_consumer_reassessment_requested: 'Reassessment triggered',
    market_recompute_requested: 'Readiness recomputed',
    market_manual_signal_updated: 'Manual signal changed',
    market_adjacency_recomputed: 'Adjacency recomputed',
  };
  return labels[key] || titleCase(key);
}

function formatAdjacentStatus(row = {}) {
  if (row.catalogStatus === 'pending_review') return 'Pending Review';
  return titleCase(row.marketStatus || row.catalogStatus);
}

function badgeToneForMarketStatus(status) {
  const key = String(status || '').trim();
  if (key === 'active') return 'green';
  if (key === 'launch_ready' || key === 'priority_development') return 'blue';
  return 'neutral';
}

function formatAdjacencyStatusSummary(statusCounts = {}) {
  const ordered = ['active', 'launch_ready', 'priority_development', 'developing', 'passive', 'out_of_scope'];
  return ordered
    .map((status) => {
      const count = Number(statusCounts?.[status] || 0);
      return count ? `${formatCount(count)} ${titleCase(status)}` : '';
    })
    .filter(Boolean)
    .join(', ');
}

export function OpsMarketsCatalog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markets, setMarkets] = useState([]);
  const [counts, setCounts] = useState({});
  const [hotspots, setHotspots] = useState([]);
  const [hotspotNote, setHotspotNote] = useState('');
  const [opsContext, setOpsContext] = useState({});
  const [filters, setFilters] = useState({
    q: '',
    state: 'all',
    catalogStatus: '',
    marketStatus: '',
    readiness: '',
    sort: 'lastActivity',
  });

  async function loadData(activeFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (activeFilters.q) params.set('q', activeFilters.q);
      if (activeFilters.state && activeFilters.state !== 'all') params.set('state', activeFilters.state);
      if (activeFilters.catalogStatus) params.set('catalogStatus', activeFilters.catalogStatus);
      if (activeFilters.marketStatus) params.set('marketStatus', activeFilters.marketStatus);
      if (activeFilters.readiness) params.set('readiness', activeFilters.readiness);
      if (activeFilters.sort) params.set('sort', activeFilters.sort);

      const [marketResp, hotspotResp] = await Promise.all([
        apiFetch(`/ops/markets?${params.toString()}`),
        apiFetch('/ops/markets/home-area-hotspots?limit=25'),
      ]);

      setMarkets(Array.isArray(marketResp?.rows) ? marketResp.rows : []);
      setCounts(marketResp?.counts || {});
      setHotspots(Array.isArray(hotspotResp?.rows) ? hotspotResp.rows : []);
      setHotspotNote(hotspotResp?.note || '');
      setOpsContext(marketResp?.opsContext || hotspotResp?.opsContext || {});
    } catch (err) {
      if (authRedirect(navigate, err)) return;
      setError(err?.data?.error || err?.message || 'Failed to load markets');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stateOptions = useMemo(() => {
    const states = new Set(['all']);
    markets.forEach((market) => {
      if (market?.stateCode) states.add(String(market.stateCode).toUpperCase());
    });
    hotspots.forEach((hotspot) => {
      if (hotspot?.stateCode) states.add(String(hotspot.stateCode).toUpperCase());
    });
    return Array.from(states);
  }, [markets, hotspots]);

  const canCreateMarkets = !!(opsContext?.canCreateMarkets ?? opsContext?.canManageMarkets);
  const setActivationReadyFilter = () => {
    const next = { ...filters, readiness: 'activation_ready' };
    setFilters(next);
    loadData(next);
  };
  const clearReadinessFilter = () => {
    const next = { ...filters, readiness: '' };
    setFilters(next);
    loadData(next);
  };

  return (
    <OpsLayout
      title="Markets"
      subtitle="Canonical market catalog, lifecycle readiness, and inferred home-area hotspots."
      headerActions={(
        <>
          {canCreateMarkets ? (
            <button className="ops-btn primary" type="button" onClick={() => navigate('/ops/markets/hotspots')}>
              Create Market
            </button>
          ) : null}
          <button className="ops-btn active" type="button" disabled>
            Market Catalog
          </button>
          <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets/hotspots')}>
            Demand Hotspots
          </button>
          <button className="ops-btn" type="button" onClick={() => loadData(filters)}>
            Refresh
          </button>
        </>
      )}
    >
      {error ? <div className="ops-message error">{error}</div> : null}

      <section className="ops-cards">
        <MetricCard title="Markets" value={formatCount(counts.total)} caption="Canonical market definitions" />
        <MetricCard title="Pending Review" value={formatCount(counts.pendingReview)} caption="Catalog status needs Ops review" />
        <MetricCard
          title="Activation Ready"
          value={formatCount(counts.activationReady)}
          caption="Readiness threshold is met"
          onClick={setActivationReadyFilter}
        />
        <MetricCard title="Demand Hotspots" value={formatCount(hotspots.length)} caption="Fresh inferred consumer home areas" />
      </section>

      <section className="ops-panel">
        <div className="ops-market-panel-header">
          <div>
            <h2>Market Catalog</h2>
            <p>One row per canonical market. Consumer-facing visibility still depends on lifecycle, not catalog status.</p>
          </div>
          <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets/hotspots')}>
            Demand Hotspots
          </button>
        </div>

        <div className="ops-market-filters">
          <input
            type="search"
            placeholder="Search market name or key"
            value={filters.q}
            onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
          />
          <select value={filters.state} onChange={(event) => setFilters((prev) => ({ ...prev, state: event.target.value }))}>
            {stateOptions.map((state) => (
              <option key={state} value={state}>{state === 'all' ? 'All States' : state}</option>
            ))}
          </select>
          <select value={filters.catalogStatus} onChange={(event) => setFilters((prev) => ({ ...prev, catalogStatus: event.target.value }))}>
            {CATALOG_STATUSES.map((status) => (
              <option key={status.key || 'all'} value={status.key}>{status.label}</option>
            ))}
          </select>
          <select value={filters.marketStatus} onChange={(event) => setFilters((prev) => ({ ...prev, marketStatus: event.target.value }))}>
            {MARKET_STATUSES.map((status) => (
              <option key={status.key || 'all'} value={status.key}>{status.label}</option>
            ))}
          </select>
          <select value={filters.sort} onChange={(event) => setFilters((prev) => ({ ...prev, sort: event.target.value }))}>
            <option value="lastActivity">Last Activity</option>
            <option value="name">Name</option>
            <option value="marketStatus">Lifecycle Status</option>
            <option value="catalogStatus">Catalog Status</option>
          </select>
          <button className="ops-btn" type="button" onClick={() => loadData(filters)}>
            Apply
          </button>
        </div>
        {filters.readiness === 'activation_ready' ? (
          <div className="ops-market-filter-chip">
            Showing activation-ready markets only.
            <button type="button" onClick={clearReadinessFilter}>Clear</button>
          </div>
        ) : null}

        {loading ? (
          <div className="ops-message">Loading markets…</div>
        ) : (
          <div className="ops-queue-table-wrap">
            <table className="ops-queue-table">
              <thead>
                <tr>
                  <th>Market</th>
                  <th>Catalog</th>
                  <th>Lifecycle</th>
                  <th>Activation Path</th>
                  <th>Supply</th>
                  <th>Consumers</th>
                  <th>Badges</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {markets.map((market) => {
                  const countsForMarket = market.entityCounts || market.counts || {};
                  const assignedConsumers = metricValue(
                    countsForMarket.assignedConsumers,
                    countsForMarket.consumers,
                    market.assignedConsumers
                  );
                  const pendingConsumers = metricValue(
                    countsForMarket.pendingConsumers,
                    market.pendingConsumers,
                    market.readiness?.pendingConsumers
                  );
                  const unresolvedConsumers = metricValue(
                    countsForMarket.unresolvedConsumers,
                    market.unresolvedConsumers,
                    market.readiness?.inferredDemand?.unresolvedConsumerCount
                  );
                  return (
                    <tr
                      key={market.marketKey}
                      className="ops-clickable-row"
                      onClick={() => navigate(`/ops/markets/${encodeURIComponent(market.marketKey)}`)}
                    >
                      <td>
                        <strong>{market.displayName || market.marketKey}</strong>
                        <div className="ops-card-caption">{market.marketKey}</div>
                      </td>
                      <td><Badge tone={market.catalogStatus === 'active' ? 'green' : 'neutral'}>{titleCase(market.catalogStatus)}</Badge></td>
                      <td><Badge tone={market.marketStatus === 'active' ? 'green' : 'blue'}>{titleCase(market.marketStatus)}</Badge></td>
                      <td>{titleCase(market.activationPath || 'standard')}</td>
                      <td>
                        {formatCount(metricValue(countsForMarket.businesses, countsForMarket.brands, market.businessCount))} businesses
                        <div className="ops-card-caption">{formatCount(metricValue(countsForMarket.nfps, countsForMarket.causes, market.nfpCount))} NFPs</div>
                      </td>
                      <td>
                        {formatCount(assignedConsumers)} assigned
                        <div className="ops-card-caption">{formatCount(pendingConsumers)} pending</div>
                        <div className="ops-card-caption">{formatCount(unresolvedConsumers)} unresolved</div>
                      </td>
                      <td>
                        <div className="ops-market-badge-row">
                          {market?.badges?.pendingReview ? <Badge>Pending Review</Badge> : null}
                          {market?.badges?.adjacencyPending ? <Badge>Adjacency Pending</Badge> : null}
                          {market?.badges?.meetsActivationThreshold ? <Badge tone="green">Ready</Badge> : null}
                        </div>
                      </td>
                      <td>{formatDate(market.lastActivityAt || market.updatedAt)}</td>
                    </tr>
                  );
                })}
                {!markets.length ? (
                  <tr>
                    <td colSpan="8">No markets matched the selected filters.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="ops-panel">
        <div className="ops-market-panel-header">
          <div>
            <h2>Demand Hotspots</h2>
            <p>{hotspotNote || 'Inferred demand signals. Not committed market assignments.'}</p>
          </div>
          <div className="ops-market-section-actions">
            <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets/hotspots')}>
              Review Hotspots
            </button>
            {canCreateMarkets ? (
              <button className="ops-btn primary" type="button" onClick={() => navigate('/ops/markets/hotspots')}>
                Create from Hotspot
              </button>
            ) : null}
            <button className="ops-btn" type="button" disabled title="Watchlist persistence is not wired yet.">
              Add to Watchlist
            </button>
          </div>
        </div>
        {hotspots.length ? (
          <HotspotsTable rows={hotspots} compact />
        ) : (
          <div className="ops-market-empty">
            <strong>No fresh demand hotspots found.</strong>
            <p>Refresh to recompute current signals, or review the hotspot surface for future watchlist support.</p>
            <div className="ops-market-section-actions">
              <button className="ops-btn" type="button" onClick={() => loadData(filters)}>Refresh</button>
              <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets/hotspots')}>Review Hotspots</button>
            </div>
          </div>
        )}
      </section>
    </OpsLayout>
  );
}

function HotspotsTable({
  rows = [],
  compact = false,
  onCreateMarket = null,
  onReviewHotspot = null,
  creatingKey = '',
}) {
  const hasActions = !!onCreateMarket || !!onReviewHotspot;
  return (
    <div className="ops-queue-table-wrap">
      <table className="ops-queue-table">
        <thead>
          <tr>
            <th>Area</th>
            <th>Type</th>
            <th>Consumers</th>
            <th>Assigned Elsewhere</th>
            <th>Pending</th>
            <th>Unassigned</th>
            {!compact ? <th>Confidence</th> : null}
            <th>Last Seen</th>
            {hasActions ? <th>Actions</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key || `${row.label}_${row.type}`}>
              <td>
                <strong>{row.label || row.key}</strong>
                <div className="ops-card-caption">{row.key}</div>
              </td>
              <td>{titleCase(row.type)}</td>
              <td>{formatCount(row.consumerCount || row.consumers)}</td>
              <td>{formatCount(row.assignedElsewhere)}</td>
              <td>{formatCount(row.pending)}</td>
              <td>{formatCount(row.unassigned)}</td>
              {!compact ? <td>{row.avgConfidence ? `${Number(row.avgConfidence * 100).toFixed(0)}%` : '—'}</td> : null}
              <td>{formatDate(row.lastObservedAt)}</td>
              {hasActions ? (
                <td>
                  <div className="ops-market-row-actions">
                    {onReviewHotspot ? (
                      <button className="ops-btn" type="button" onClick={() => onReviewHotspot(row)}>
                        Review
                      </button>
                    ) : null}
                    {onCreateMarket ? (
                      <button
                        className="ops-btn primary"
                        type="button"
                        disabled={creatingKey === row.key}
                        onClick={() => onCreateMarket(row)}
                      >
                        {creatingKey === row.key ? 'Creating…' : 'Create Market'}
                      </button>
                    ) : null}
                    <button className="ops-btn" type="button" disabled title="Watchlist persistence is not wired yet.">
                      Watchlist
                    </button>
                  </div>
                </td>
              ) : null}
            </tr>
          ))}
          {!rows.length ? (
            <tr>
              <td colSpan={(compact ? 7 : 8) + (hasActions ? 1 : 0)}>No fresh demand hotspots found.</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export function OpsMarketHotspots() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState('');
  const [creatingKey, setCreatingKey] = useState('');
  const [opsContext, setOpsContext] = useState({});

  async function loadHotspots() {
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch('/ops/markets/home-area-hotspots?limit=100');
      setRows(Array.isArray(resp?.rows) ? resp.rows : []);
      setNote(resp?.note || '');
      setOpsContext(resp?.opsContext || {});
    } catch (err) {
      if (authRedirect(navigate, err)) return;
      setError(err?.data?.error || err?.message || 'Failed to load home-area hotspots');
    } finally {
      setLoading(false);
    }
  }

  function reviewHotspot(row = {}) {
    const label = row.label || row.key || 'Hotspot';
    setMessage(`${label}: ${formatCount(row.consumerCount || row.consumers)} consumers, ${formatCount(row.pending)} pending, ${formatCount(row.unassigned)} unassigned.`);
  }

  async function createMarketFromHotspot(row = {}) {
    const key = String(row.key || '').trim();
    const geoFacts = row.geoFacts || {};
    let label = String(row.label || '').trim();
    let locality = String(geoFacts.locality || '').trim();
    let stateCode = String(geoFacts.state || '').trim().toUpperCase();

    if (!locality || !stateCode) {
      const fallback = label.includes(',') ? label : '';
      const input = window.prompt('Enter canonical market name as City, ST', fallback);
      if (!input) return;
      label = input.trim();
      const parts = label.split(',');
      locality = String(parts[0] || '').trim();
      stateCode = String(parts[1] || '').trim().toUpperCase();
    }

    if (!locality || !stateCode) {
      setError('A canonical City, ST is required to create a market from this hotspot.');
      return;
    }

    setCreatingKey(key);
    setError('');
    setMessage('');
    try {
      const resp = await apiFetch('/ops/markets/from-hotspot', {
        method: 'POST',
        body: {
          hotspotKey: key,
          label: label || `${locality}, ${stateCode}`,
          locality,
          stateCode,
          county: geoFacts.county || '',
          countryCode: geoFacts.country || 'US',
          geoFacts,
          aliases: [
            ...(geoFacts.zipCode ? [{ aliasKey: geoFacts.zipCode, aliasType: 'zip' }] : []),
          ],
        },
      });
      const reassessment = resp?.consumerReassessment || {};
      setMessage(`Created ${resp?.market?.displayName || label}. Reassessment scanned ${formatCount(reassessment.scanned)} and changed ${formatCount(reassessment.changed)} consumers.`);
      await loadHotspots();
      if (resp?.market?.marketKey) {
        navigate(`/ops/markets/${encodeURIComponent(resp.market.marketKey)}`);
      }
    } catch (err) {
      if (authRedirect(navigate, err)) return;
      setError(err?.data?.error || err?.message || 'Failed to create market from hotspot');
    } finally {
      setCreatingKey('');
    }
  }

  useEffect(() => {
    loadHotspots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OpsLayout
      title="Demand Hotspots"
      subtitle={note || 'Inferred consumer home-area clusters that may not be canonical markets yet.'}
      headerActions={(
        <>
          <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets')}>
            Market Catalog
          </button>
          <button className="ops-btn active" type="button" disabled>
            Demand Hotspots
          </button>
          <button className="ops-btn" type="button" onClick={loadHotspots}>
            Refresh
          </button>
        </>
      )}
    >
      {error ? <div className="ops-message error">{error}</div> : null}
      {message ? <div className="ops-message">{message}</div> : null}
      {loading ? <div className="ops-message">Loading hotspots…</div> : (
        <section className="ops-panel">
          <h2>Fresh Inferred Demand</h2>
          <p>Inferred consumer geography for BD review. These rows are not committed assignments.</p>
          {rows.length ? (
            <HotspotsTable
              rows={rows}
              onCreateMarket={(opsContext?.canCreateMarkets ?? opsContext?.canManageMarkets) ? createMarketFromHotspot : null}
              onReviewHotspot={reviewHotspot}
              creatingKey={creatingKey}
            />
          ) : (
            <div className="ops-market-empty">
              <strong>No fresh demand hotspots found.</strong>
              <p>Refresh current signals or return to the Market Catalog.</p>
              <div className="ops-market-section-actions">
                <button className="ops-btn" type="button" onClick={loadHotspots}>Refresh</button>
                <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets')}>Market Catalog</button>
              </div>
            </div>
          )}
        </section>
      )}
    </OpsLayout>
  );
}

export function OpsMarketDetail() {
  const navigate = useNavigate();
  const { marketKey } = useParams();
  const decodedMarketKey = decodeURIComponent(marketKey || '');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // D44: each sampled consumer's outcome, why they did not commit, and their evidence.
  const [reassessmentSamples, setReassessmentSamples] = useState([]);
  const [data, setData] = useState(null);
  const [transitionStatus, setTransitionStatus] = useState('');
  const [transitionReason, setTransitionReason] = useState('');
  const [catalogStatus, setCatalogStatus] = useState('');
  const [catalogReason, setCatalogReason] = useState('');
  const [includeAssigned, setIncludeAssigned] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [aliasKey, setAliasKey] = useState('');
  const [aliasType, setAliasType] = useState('locality');
  const [entityDrawer, setEntityDrawer] = useState(null);

  async function loadDetail() {
    if (!decodedMarketKey) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}`);
      setData(resp || null);
    } catch (err) {
      if (authRedirect(navigate, err)) return;
      setError(err?.data?.error || err?.message || 'Failed to load market detail');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedMarketKey]);

  async function runAction(actionKey, request) {
    setActionLoading(actionKey);
    setError('');
    setMessage('');
    setReassessmentSamples([]);
    try {
      const resp = await request();
      setReassessmentSamples(Array.isArray(resp?.samples) ? resp.samples : []);
      const summary = resp?.consumerReassessment || resp?.summary;
      const summaryText = summary
        ? ` Consumer reassessment: scanned ${formatCount(summary.scanned)}, changed ${formatCount(summary.changed)}, report-only ${formatCount(summary.reportOnly)}.`
        : '';
      // D44: why consumers stayed pending; no_resolved_market is the coverage gap.
      const held = Object.entries(summary?.held || {});
      const heldText = held.length
        ? ` Held: ${held.map(([reason, count]) => `${reason.replace(/_/g, ' ')} ${formatCount(count)}`).join(' · ')}.`
        : '';
      setMessage(`${titleCase(actionKey)} complete.${summaryText}${heldText}`);
      await loadDetail();
    } catch (err) {
      if (authRedirect(navigate, err)) return;
      setError(err?.data?.error || err?.message || `Failed to run ${actionKey}`);
    } finally {
      setActionLoading('');
    }
  }

  async function addAlias(event) {
    event.preventDefault();
    if (!aliasKey.trim() || !aliasType) return;
    await runAction('alias update', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/aliases`, {
      method: 'POST',
      body: {
        aliasKey: aliasKey.trim(),
        aliasType,
        source: 'manual_admin',
      },
    }));
    setAliasKey('');
    setAliasType('locality');
  }

  async function deleteAlias(aliasId) {
    if (!aliasId) return;
    await runAction('alias delete', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/aliases/${encodeURIComponent(aliasId)}`, {
      method: 'DELETE',
    }));
  }

  const market = data?.market || {};
  const readiness = data?.readiness || {};
  const entities = data?.entities || {};
  const adjacency = data?.adjacency || {};
  const adjacentRows = Array.isArray(adjacency.rows) ? adjacency.rows : [];
  const availableTransitions = Array.isArray(data?.availableTransitions) ? data.availableTransitions : [];
  const opsContext = data?.opsContext || {};
  const canManageMarketOperations = !!opsContext?.canManageMarketOperations;
  const canManageMarketLifecycle = !!opsContext?.canManageMarketLifecycle;
  const entityCounts = entities?.counts || {};
  const adjacencyStatusSummary = formatAdjacencyStatusSummary(adjacency.statusCounts || {});
  const transitionReadinessWarning = transitionStatus
    && ['launch_ready', 'active'].includes(transitionStatus)
    && !readiness?.meetsActivationThreshold
    ? 'Readiness does not currently support this move. Use an explicit reason if leadership is overriding the readiness signal.'
    : '';

  return (
    <OpsLayout
      title={market.displayName || decodedMarketKey || 'Market Detail'}
      subtitle={(
        <div className="ops-market-detail-meta">
          <span>Created {formatShortDate(market.createdAt)}</span>
          <span>Touched {formatCount(adjacency.count || adjacentRows.length)} markets</span>
          {adjacencyStatusSummary ? <span>{adjacencyStatusSummary}</span> : null}
          <span>Market Key · {decodedMarketKey}</span>
        </div>
      )}
      headerActions={(
        <>
          <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets')}>
            Market Catalog
          </button>
          <button className="ops-btn" type="button" onClick={() => navigate('/ops/markets/hotspots')}>
            Demand Hotspots
          </button>
          <button className="ops-btn" type="button" onClick={loadDetail}>
            Refresh
          </button>
        </>
      )}
    >
      {error ? <div className="ops-message error">{error}</div> : null}
      {message ? <div className="ops-message">{message}</div> : null}
      {reassessmentSamples.length ? (
        <div className="ops-message">
          <table className="ops-queue-table">
            <thead>
              <tr><th>Consumer</th><th>Outcome</th><th>Why not committed</th><th>Evidence (trailing window)</th></tr>
            </thead>
            <tbody>
              {reassessmentSamples.map((sample) => {
                const top = sample.evidence?.topMarkets?.[0];
                return (
                  <tr key={sample.consumerId}>
                    <td>{sample.consumerId}</td>
                    <td>
                      {sample.mode === 'apply' && sample.nextMarketKey
                        ? `${sample.nextMarketKey} (${sample.commitBasis || 'existing'})`
                        : sample.mode === 'apply' ? 'pending' : sample.mode}
                    </td>
                    <td>{sample.reason || '—'}</td>
                    <td>
                      {sample.evidence
                        ? `${formatCount(sample.evidence.totalLocated)} located in-store${top
                          ? ` · ${top.marketKey}: ${top.transactions} tx, ${top.days} days, ${top.merchants} merchants, ${Math.round((top.share || 0) * 100)}% (${Math.round((top.shareOfResolved || 0) * 100)}% of resolved)`
                          : ''}${sample.evidence.noMarketRemainder?.transactions
                          ? ` · no market: ${sample.evidence.noMarketRemainder.transactions} tx in ${sample.evidence.noMarketRemainder.distinctTowns} town${sample.evidence.noMarketRemainder.distinctTowns === 1 ? '' : 's'}${sample.evidence.noMarketRemainder.towns?.[0] ? ` (top: ${sample.evidence.noMarketRemainder.towns[0].town})` : ''}`
                          : ''}`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {loading ? <div className="ops-message">Loading market detail…</div> : null}

      {!loading && data ? (
        <>
          <section className="ops-cards">
            <MetricCard title="Catalog Status" value={titleCase(market.catalogStatus)} caption="Catalog/review health only" />
            <MetricCard title="Lifecycle" value={titleCase(market.marketStatus)} caption="Controls visibility and activation" />
            <MetricCard title="Activation Path" value={titleCase(market.activationPath || 'standard')} caption="Funding acceleration path" />
            <MetricCard title="Readiness" value={readiness?.meetsActivationThreshold ? 'Ready' : 'Not Ready'} caption="Activation threshold state" />
          </section>

          <section className="ops-panel">
            <div className="ops-market-panel-header">
              <div>
                <h2>Adjacent Markets</h2>
                <p>Configured neighboring markets used for Ops review and activation-path context.</p>
              </div>
            </div>
            {adjacentRows.length ? (
              <div className="ops-queue-table-wrap">
                <table className="ops-queue-table">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>Status</th>
                      <th>Boundary / Overlap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjacentRows.map((row) => (
                      <tr
                        key={row.marketKey}
                        className="ops-clickable-row"
                        onClick={() => navigate(`/ops/markets/${encodeURIComponent(row.marketKey)}`)}
                      >
                        <td>
                          <strong>{row.displayName || row.marketKey}</strong>
                          <div className="ops-card-caption">{row.marketKey}</div>
                        </td>
                        <td>
                          <Badge tone={badgeToneForMarketStatus(row.catalogStatus === 'pending_review' ? 'pending_review' : row.marketStatus)}>
                            {formatAdjacentStatus(row)}
                          </Badge>
                        </td>
                        <td>
                          {titleCase(row.boundaryType || 'configured')}
                          <div className="ops-card-caption">{row.overlapNote || 'Configured adjacency'}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="ops-market-empty">
                <strong>No adjacent markets configured.</strong>
                <p>Add adjacency metadata before Ops can inspect neighboring market relationships here.</p>
              </div>
            )}
          </section>

          <section className="ops-panel">
            <h2>Market Operations</h2>
            <p>Reassessment targets unresolved consumers by default. Assigned consumers can be included as report-only candidates.</p>
            <div className="ops-market-transition">
              <select value={catalogStatus} onChange={(event) => setCatalogStatus(event.target.value)}>
                <option value="">Select catalog status</option>
                {CATALOG_STATUSES.filter((status) => status.key).map((status) => (
                  <option key={status.key} value={status.key}>
                    {status.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Required catalog review reason"
                value={catalogReason}
                onChange={(event) => setCatalogReason(event.target.value)}
              />
              <button
                className="ops-btn"
                type="button"
                disabled={!canManageMarketLifecycle || !catalogStatus || !catalogReason.trim() || !!actionLoading}
                onClick={() => runAction('catalog status', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/catalog-status`, {
                  method: 'PATCH',
                  body: { catalogStatus, reason: catalogReason.trim() },
                })).then(() => {
                  setCatalogStatus('');
                  setCatalogReason('');
                })}
              >
                {actionLoading === 'catalog status' ? 'Updating Catalog…' : 'Apply Catalog Status'}
              </button>
            </div>
            <div className="ops-market-actions">
              <button
                className="ops-btn"
                type="button"
                disabled={!canManageMarketOperations || !!actionLoading}
                onClick={() => runAction('recompute adjacency', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/recompute-adjacency`, { method: 'POST', body: {} }))}
              >
                {actionLoading === 'recompute adjacency' ? 'Recomputing adjacency…' : 'Recompute Adjacency'}
              </button>
              <button
                className="ops-btn"
                type="button"
                disabled={!canManageMarketOperations || !!actionLoading}
                onClick={() => runAction('recompute', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/recompute`, { method: 'POST', body: {} }))}
              >
                {actionLoading === 'recompute' ? 'Recomputing…' : 'Recompute Readiness'}
              </button>
              <label className="ops-market-checkbox">
                <input type="checkbox" checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />
                Dry run
              </label>
              <label className="ops-market-checkbox">
                <input type="checkbox" checked={includeAssigned} onChange={(event) => setIncludeAssigned(event.target.checked)} />
                Include assigned as report-only
              </label>
              <button
                className="ops-btn primary"
                type="button"
                disabled={!canManageMarketOperations || !!actionLoading}
                onClick={() => runAction('reassess consumers', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/reassess-consumers`, {
                  method: 'POST',
                  body: { apply: !dryRun, includeAssigned, limit: 500 },
                }))}
              >
                {actionLoading === 'reassess consumers' ? 'Running backfill…' : 'Run targeted backfill'}
              </button>
            </div>

            <div className="ops-market-transition">
              <select value={transitionStatus} onChange={(event) => setTransitionStatus(event.target.value)}>
                <option value="">Select lifecycle transition</option>
                {availableTransitions.map((transition) => (
                  <option key={transition.toStatus} value={transition.toStatus} disabled={!transition.allowed}>
                    {titleCase(transition.toStatus)}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Required transition reason"
                value={transitionReason}
                onChange={(event) => setTransitionReason(event.target.value)}
              />
              <button
                className="ops-btn"
                type="button"
                disabled={!transitionStatus || !transitionReason.trim() || !!actionLoading}
                onClick={() => runAction('transition', () => apiFetch(`/ops/markets/${encodeURIComponent(decodedMarketKey)}/transition`, {
                  method: 'POST',
                  body: { toStatus: transitionStatus, reason: transitionReason.trim() },
                }))}
              >
                {actionLoading === 'transition' ? 'Transitioning…' : 'Apply Transition'}
              </button>
            </div>
            {transitionReadinessWarning ? (
              <div className="ops-market-warning">{transitionReadinessWarning}</div>
            ) : null}
          </section>

          <section className="ops-panel">
            <h2>Supply and Consumers</h2>
            <div className="ops-cards">
              <MetricCard title="Businesses" value={formatCount(entities?.counts?.businesses || entities?.businesses?.length)} caption="Sample linked below" />
              <MetricCard title="NFPs" value={formatCount(entities?.counts?.nfps || entities?.nfps?.length)} caption="Sample linked below" />
              <MetricCard title="Assigned Consumers" value={formatCount(entities?.consumerCount || readiness?.consumerCount)} caption="Committed market only" />
              <MetricCard title="Pending Candidates" value={formatCount(readiness?.pendingConsumerCandidateCount || readiness?.signals?.pendingConsumerCandidate?.count)} caption="Inferred, not committed" />
              <MetricCard
                title="Unresolved Matching This Market"
                value={formatCount(entityCounts.unresolvedConsumers || readiness?.inferredDemand?.unresolvedConsumerCount)}
                caption="Pending or unassigned candidates"
              />
            </div>
            <EntityTables entities={entities} navigate={navigate} onOpenDrawer={setEntityDrawer} />
          </section>

          <section className="ops-panel">
            <h2>Aliases</h2>
            <p>Aliases let raw legacy values, locality strings, ZIPs, or external keys resolve to this canonical market. They do not change lifecycle status.</p>
            <form className="ops-market-alias-form" onSubmit={addAlias}>
              <input
                type="text"
                placeholder="Alias key, e.g. lake forest or 60045"
                value={aliasKey}
                onChange={(event) => setAliasKey(event.target.value)}
              />
              <select value={aliasType} onChange={(event) => setAliasType(event.target.value)}>
                <option value="zip">ZIP alias</option>
                <option value="locality">Locality alias</option>
                <option value="legacy_string">Legacy locality key</option>
                <option value="county">County alias</option>
                <option value="state">State alias</option>
                <option value="external_market">External market key</option>
              </select>
              <button className="ops-btn" type="submit" disabled={!canManageMarketOperations || !aliasKey.trim() || !!actionLoading}>
                Add Alias
              </button>
            </form>
            <div className="ops-queue-table-wrap">
              <table className="ops-queue-table">
                <thead>
                  <tr>
                    <th>Alias</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(data.aliases) ? data.aliases : []).map((alias) => (
                    <tr key={alias.id || alias.aliasKey}>
                      <td><strong>{alias.aliasKey}</strong></td>
                      <td>{formatAliasType(alias.aliasType)}</td>
                      <td>{formatAliasSource(alias.source)}</td>
                      <td>{formatDate(alias.updatedAt || alias.createdAt)}</td>
                      <td>
                        <button
                          className="ops-btn"
                          type="button"
                          disabled={!canManageMarketOperations || !!actionLoading}
                          onClick={() => deleteAlias(alias.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!data.aliases?.length ? (
                    <tr>
                      <td colSpan="5">No aliases are currently mapped to this market.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          <section className="ops-panel">
            <h2>Audit History</h2>
            <div className="ops-queue-table-wrap">
              <table className="ops-queue-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event</th>
                    <th>Actor</th>
                    <th>Metadata</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(data.auditTimeline) ? data.auditTimeline : []).map((event) => (
                    <tr key={event.id}>
                      <td>{formatDate(event.createdAt)}</td>
                      <td>{formatAuditEvent(event.eventType)}</td>
                      <td>{event.actorUserId || 'system'}</td>
                      <td><code>{JSON.stringify(event.metadata || {})}</code></td>
                    </tr>
                  ))}
                  {!data.auditTimeline?.length ? (
                    <tr>
                      <td colSpan="4">No audit events found for this market.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
      {entityDrawer ? (
        <EntityDrawer drawer={entityDrawer} onClose={() => setEntityDrawer(null)} navigate={navigate} />
      ) : null}
    </OpsLayout>
  );
}

function EntityTables({ entities = {}, navigate, onOpenDrawer }) {
  const businesses = Array.isArray(entities.businesses) ? entities.businesses : [];
  const nfps = Array.isArray(entities.nfps) ? entities.nfps : Array.isArray(entities.causes) ? entities.causes : [];
  const consumers = Array.isArray(entities.consumers) ? entities.consumers : [];

  return (
    <div className="ops-market-entity-grid">
      <MiniEntityTable
        title="Businesses"
        rows={businesses}
        empty="No businesses linked to this market."
        onRowClick={(row) => row?.id && navigate(`/ops/businesses/${row.id}`)}
        onViewAll={() => onOpenDrawer({ title: 'Businesses', rows: businesses, type: 'businesses' })}
      />
      <MiniEntityTable
        title="NFPs"
        rows={nfps}
        empty="No NFPs linked to this market."
        onRowClick={(row) => row?.id && navigate(`/ops/nfps/${row.id}`)}
        onViewAll={() => onOpenDrawer({ title: 'NFPs', rows: nfps, type: 'nfps' })}
      />
      <MiniEntityTable
        title="Consumers"
        rows={consumers}
        empty="No committed consumers sampled for this market."
        onViewAll={() => onOpenDrawer({ title: 'Consumers', rows: consumers, type: 'consumers' })}
      />
    </div>
  );
}

function MiniEntityTable({ title, rows = [], empty, onRowClick, onViewAll }) {
  const previewRows = rows.slice(0, 5);
  return (
    <div className="ops-market-entity-card">
      <div className="ops-market-entity-card-header">
        <h3 className="ops-market-subheading">{title}</h3>
        {rows.length ? (
          <button className="ops-btn" type="button" onClick={onViewAll}>
            View all
          </button>
        ) : null}
      </div>
      <div className="ops-queue-table-wrap">
        <table className="ops-queue-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row) => (
              <tr
                key={row.id || row._id || row.name}
                className={onRowClick ? 'ops-clickable-row' : ''}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                <td>
                  <strong>{row.name || row.displayName || row.email || row.id || 'Record'}</strong>
                  {row.id ? <div className="ops-card-caption">{row.id}</div> : null}
                </td>
                <td>{titleCase(row.status || row.lifecycleStatus || row.registrationStatus || row.marketAssignmentStatus)}</td>
                <td>{formatDate(row.updatedAt || row.createdAt)}</td>
              </tr>
            ))}
            {!previewRows.length ? (
              <tr>
                <td colSpan="3">{empty}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EntityDrawer({ drawer, onClose, navigate }) {
  const rows = Array.isArray(drawer?.rows) ? drawer.rows : [];
  const title = drawer?.title || 'Records';
  const isConsumers = drawer?.type === 'consumers';

  return (
    <div className="ops-market-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside className="ops-market-drawer" role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <div className="ops-market-drawer-header">
          <div>
            <h2>{title}</h2>
            <p>{isConsumers ? 'Preview list. Add search/filter here if this grows.' : 'Preview list for quick Ops review.'}</p>
          </div>
          <button className="ops-btn" type="button" onClick={onClose}>Close</button>
        </div>
        <div className="ops-queue-table-wrap">
          <table className="ops-queue-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id || row._id || row.name}
                  className={drawer?.type === 'businesses' || drawer?.type === 'nfps' ? 'ops-clickable-row' : ''}
                  onClick={() => {
                    if (drawer?.type === 'businesses' && row?.id) navigate(`/ops/businesses/${row.id}`);
                    if (drawer?.type === 'nfps' && row?.id) navigate(`/ops/nfps/${row.id}`);
                  }}
                >
                  <td>
                    <strong>{row.name || row.displayName || row.email || row.id || 'Record'}</strong>
                    {row.id ? <div className="ops-card-caption">{row.id}</div> : null}
                  </td>
                  <td>{titleCase(row.status || row.lifecycleStatus || row.registrationStatus || row.marketAssignmentStatus)}</td>
                  <td>{formatDate(row.updatedAt || row.createdAt)}</td>
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan="3">No rows available.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </aside>
    </div>
  );
}
